import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import {
  useLocation,
  useParams,
} from "react-router-dom";

import {
  Organisation,
} from "api/Accounts/AccountsInterfaces";
import { backendOrigin } from "api/APIClient";
import {
  conversationsAPI,
} from "api/Interactions/ConversationsAPI";
import {
  eventsAPI,
} from "api/Interactions/EventsAPI";

const Navbar_External: React.FC = () => {
  const theme = useTheme();

  const { token } = useParams<{
    token: string;
  }>();

  const location = useLocation();

  const [organisation, setOrganisation] =
    useState<Organisation | null>(null);

  useEffect(() => {
    if (!token) {
      setOrganisation(null);
      return;
    }

    let isMounted = true;

    const loadOrganisation = async () => {
      try {
        const pathname =
          location.pathname.toLowerCase();

        const isEventPage =
          pathname.includes("/event/");

        const isConversationPage =
          pathname.includes("/conversations/");

        if (
          !isEventPage &&
          !isConversationPage
        ) {
          if (isMounted) {
            setOrganisation(null);
          }

          return;
        }

        const event = isEventPage
          ? await eventsAPI.getByToken(token)
          : null;

        const conversation =
          isConversationPage
            ? await conversationsAPI.getByToken(
                token
              )
            : null;

        const loadedOrganisation =
          event?.organisation ??
          conversation?.organisation ??
          null;

        if (isMounted) {
          setOrganisation(
            loadedOrganisation
          );
        }
      } catch (error) {
        console.error(
          "Navbar failed to load organisation:",
          error
        );

        if (isMounted) {
          setOrganisation(null);
        }
      }
    };

    loadOrganisation();

    return () => {
      isMounted = false;
    };
  }, [token, location.pathname]);

  const logo = organisation?.logo;

  const logoUrl = useMemo(() => {
    if (typeof logo === "string") {
      return /^https?:\/\//i.test(logo)
        ? logo
        : `${backendOrigin}${logo}`;
    }

    if (logo instanceof File) {
      return URL.createObjectURL(logo);
    }

    return null;
  }, [logo]);

  useEffect(() => {
    return () => {
      if (
        logo instanceof File &&
        logoUrl
      ) {
        URL.revokeObjectURL(logoUrl);
      }
    };
  }, [logo, logoUrl]);

  const website =
    organisation?.website?.trim() ||
    null;

  const email =
    organisation?.email?.trim() ||
    null;

  const phoneNumber =
    organisation?.phone_number?.trim() ||
    null;

  const websiteHref = website
    ? /^https?:\/\//i.test(website)
      ? website
      : `https://${website}`
    : null;

  const websiteLabel = website
    ? website
        .replace(/^https?:\/\//i, "")
        .replace(/\/$/, "")
    : null;

  const phoneHref = phoneNumber
    ? `tel:${phoneNumber.replace(
        /[^\d+]/g,
        ""
      )}`
    : null;

  const linkSx = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",

    gap: {
      xs: 0,
      md: 0.75,
    },

    minWidth: {
      xs: 40,
      md: 0,
    },

    minHeight: 40,

    px: {
      xs: 1,
      md: 1,
    },

    py: 0.75,

    color: "inherit",
    textDecoration: "none",
    borderRadius: 1.5,

    transition:
      "background-color 160ms ease, color 160ms ease",

    "&:hover": {
      backgroundColor:
        "rgba(255, 255, 255, 0.1)",
    },

    "&:focus-visible": {
      outline:
        "2px solid currentColor",
      outlineOffset: "2px",
    },
  };

  const iconSx = {
    fontSize: 20,
    flexShrink: 0,
  };

  const textSx = {
    display: {
      xs: "none",
      md: "block",
    },

    minWidth: 0,
    maxWidth: {
      md: 180,
      lg: 260,
    },

    fontSize: "0.875rem",
    fontWeight: 500,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const logoContent = logoUrl ? (
    <Box
      component="img"
      src={logoUrl}
      alt={
        organisation?.name
          ? `${organisation.name} logo`
          : "Organisation logo"
      }
      sx={{
        display: "block",

        height: {
          xs: 42,
          sm: 56,
          md: 70,
        },

        maxWidth: {
          xs: 120,
          sm: 180,
          md: 260,
        },

        width: "auto",
        objectFit: "contain",
      }}
    />
  ) : organisation?.name ? (
    <Typography
      variant="h6"
      component="span"
      sx={{
        maxWidth: {
          xs: 130,
          sm: 190,
          md: 280,
        },

        fontSize: {
          xs: "0.95rem",
          sm: "1.1rem",
          md: "1.25rem",
        },

        fontWeight: 700,
        lineHeight: 1.2,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {organisation.name}
    </Typography>
  ) : null;

  return (
    <AppBar
      position="sticky"
      sx={{
        background:
          theme.custom.ui.appBar.main
            .light,

        backdropFilter: "blur(12px)",

        color:
          theme.custom.ui.appBar.text
            .light,

        boxShadow:
          "0 3px 16px rgba(0, 0, 0, 0.28)",
      }}
    >
      <Toolbar
        sx={{
          minHeight: {
            xs: 58,
            sm: 68,
            md: 75,
          },

          px: {
            xs: 1.25,
            sm: 2,
            md: 3,
          },

          display: "flex",
          alignItems: "center",
          justifyContent:
            "space-between",

          gap: {
            xs: 0.75,
            sm: 2,
          },
        }}
      >
        {websiteHref ? (
          <Box
            component="a"
            href={websiteHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Visit ${
              organisation?.name ??
              "organisation"
            } website`}
            title={
              organisation?.name
                ? `Visit ${organisation.name} website`
                : "Visit organisation website"
            }
            sx={{
              display: "inline-flex",
              alignItems: "center",

              minWidth: 0,
              flexShrink: 1,

              color: "inherit",
              textDecoration: "none",
              borderRadius: 1.5,

              transition:
                "opacity 160ms ease",

              "&:hover": {
                opacity: 0.85,
              },

              "&:focus-visible": {
                outline:
                  "2px solid currentColor",
                outlineOffset: "4px",
              },
            }}
          >
            {logoContent}
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: 0,
              flexShrink: 1,
            }}
          >
            {logoContent}
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            flex: 1,
            minWidth: 0,

            alignItems: "center",
            justifyContent: "flex-end",

            flexWrap: "nowrap",

            gap: {
              xs: 0.25,
              sm: 0.5,
              md: 1.5,
            },
          }}
        >
          {websiteHref &&
            websiteLabel && (
              <Box
                component="a"
                href={websiteHref}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Visit ${websiteLabel}`}
                title={websiteLabel}
                sx={linkSx}
              >
                <LanguageOutlinedIcon
                  sx={iconSx}
                />

                <Typography
                  component="span"
                  sx={textSx}
                >
                  {websiteLabel}
                </Typography>
              </Box>
            )}

          {email && (
            <Box
              component="a"
              href={`mailto:${email}`}
              aria-label={`Email ${email}`}
              title={email}
              sx={linkSx}
            >
              <EmailOutlinedIcon
                sx={iconSx}
              />

              <Typography
                component="span"
                sx={textSx}
              >
                {email}
              </Typography>
            </Box>
          )}

          {phoneNumber &&
            phoneHref && (
              <Box
                component="a"
                href={phoneHref}
                aria-label={`Call ${phoneNumber}`}
                title={phoneNumber}
                sx={linkSx}
              >
                <PhoneOutlinedIcon
                  sx={iconSx}
                />

                <Typography
                  component="span"
                  sx={textSx}
                >
                  {phoneNumber}
                </Typography>
              </Box>
            )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar_External;

